import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingType } from '@/modules/settings/types/setting-type';
import {
  AuthenticatedRoutes,
  CommonRoutes,
  DataReconciliationRoutes,
  OnboardingRoutes,
  Routes,
  SettingsRoutes,
  UnauthenticatedRoutes,
} from '@/router/routes';

import { PermissionRequirement } from '../types/permission-requirement';

/**
 * Defines permission requirements for each route in the application
 * Empty policies array means accessible to all authenticated users
 */
export const ROUTE_PERMISSIONS: Record<Routes, PermissionRequirement> = {
  // ========== Authenticated Routes ==========

  // Dashboard - accessible to all authenticated users
  [AuthenticatedRoutes.Dashboard]: new PermissionRequirement({
    // TODO[out-of-scope]: refine permission once the feature is implemented
    denyAlways: true,
  }),

  // Planner - requires viewing assignments (own or all)
  [AuthenticatedRoutes.Planner]: new PermissionRequirement({
    policies: [OpsiumPolicy.AssignmentsViewOwn, OpsiumPolicy.AssignmentsViewAll],
  }),

  // Time Tracking - requires viewing worklogs AND showTimeTracking feature flag
  [AuthenticatedRoutes.TimeTracking]: new PermissionRequirement({
    policies: [OpsiumPolicy.WorklogsView],
    featureFlags: ['showTimeTracking'],
  }),

  // Time Off - requires TimeOffsView policy AND showTimeOffs feature flag
  [AuthenticatedRoutes.TimeOff]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsView],
    featureFlags: ['showTimeOffs'],
  }),

  // Time Off request details - same gate as the list
  [AuthenticatedRoutes.TimeOffRequestDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsView],
    featureFlags: ['showTimeOffs'],
  }),

  // Create a time-off request - requires the request policy
  [AuthenticatedRoutes.TimeOffRequestCreate]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsRequest],
    featureFlags: ['showTimeOffs'],
  }),

  // Approve-mode details (opened from the approvals list) - requires approve policy
  [AuthenticatedRoutes.TimeOffApprovalDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsApprove],
    featureFlags: ['showTimeOffs'],
  }),

  // Team member time-off request details (manager view of another member)
  [AuthenticatedRoutes.TeamMemberTimeOffRequestDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsViewOthers],
    featureFlags: ['showTimeOffs'],
  }),

  // Time Off Approvals - requires the approve policy AND showTimeOffs feature flag
  [AuthenticatedRoutes.TimeOffApprovals]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffsApprove],
    featureFlags: ['showTimeOffs'],
  }),

  // Time Tracking Approvals - requires viewing or approving worklogs AND showTimeTracking feature flag
  [AuthenticatedRoutes.TimeTrackingApprovals]: new PermissionRequirement({
    policies: [OpsiumPolicy.WorklogsApprovalsView, OpsiumPolicy.WorklogsApprove],
    featureFlags: ['showTimeTracking'],
  }),

  // Team - requires viewing team members
  [AuthenticatedRoutes.Team]: new PermissionRequirement({
    policies: [OpsiumPolicy.TeamMembersViewAll],
  }),

  [AuthenticatedRoutes.TeamAddMember]: new PermissionRequirement({
    policies: [OpsiumPolicy.TeamMembersCreate],
  }),

  [AuthenticatedRoutes.TeamMemberDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.TeamMembersViewAll],
  }),

  // Team Member Change Edit - requires team member editing
  [AuthenticatedRoutes.TeamMemberChangeEdit]: new PermissionRequirement({
    policies: [OpsiumPolicy.TeamMembersEdit],
  }),

  // Projects - requires viewing projects
  [AuthenticatedRoutes.Projects]: new PermissionRequirement({
    policies: [OpsiumPolicy.ProjectsViewAll],
  }),

  [AuthenticatedRoutes.AddProject]: new PermissionRequirement({
    policies: [OpsiumPolicy.ProjectsCreate],
  }),

  [AuthenticatedRoutes.ProjectDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.ProjectsViewAll],
  }),

  [AuthenticatedRoutes.ProjectChangeEdit]: new PermissionRequirement({
    policies: [OpsiumPolicy.ProjectsEdit],
  }),

  // Clients - requires viewing clients
  [AuthenticatedRoutes.Clients]: new PermissionRequirement({
    policies: [OpsiumPolicy.ClientsViewAll],
  }),

  [AuthenticatedRoutes.AddClient]: new PermissionRequirement({
    policies: [OpsiumPolicy.ClientsCreate],
  }),

  [AuthenticatedRoutes.ClientDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.ClientsViewAll],
  }),

  [AuthenticatedRoutes.Invoices]: new PermissionRequirement({
    policies: [OpsiumPolicy.InvoicesView],
  }),

  // Invoice details - same view gate as the invoices list.
  [AuthenticatedRoutes.InvoiceDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.InvoicesView],
  }),

  [AuthenticatedRoutes.GenerateInvoices]: new PermissionRequirement({
    policies: [OpsiumPolicy.InvoicesGenerate],
  }),

  [AuthenticatedRoutes.Bills]: new PermissionRequirement({
    policies: [OpsiumPolicy.BillsView],
  }),

  [AuthenticatedRoutes.BillDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.BillsView],
  }),

  [AuthenticatedRoutes.GenerateBills]: new PermissionRequirement({
    policies: [OpsiumPolicy.BillsGenerate],
  }),

  // Overheads - requires OverheadsView policy AND showOverheads feature flag
  [AuthenticatedRoutes.Overheads]: new PermissionRequirement({
    policies: [OpsiumPolicy.OverheadsView],
    featureFlags: ['showOverheads'],
  }),

  // Settings - accessible to all authenticated users
  [AuthenticatedRoutes.Settings]: new PermissionRequirement({
    policies: [],
  }),

  // Reports - accessible to all authenticated users when feature flag is enabled
  [AuthenticatedRoutes.Reports]: new PermissionRequirement({
    policies: [],
    featureFlags: ['showReports'],
  }),

  [AuthenticatedRoutes.ReportDetails]: new PermissionRequirement({
    policies: [],
    featureFlags: ['showReports'],
  }),

  // Data Reconciliation - requires integrations management
  [AuthenticatedRoutes.DataReconciliation]: new PermissionRequirement({
    policies: [OpsiumPolicy.IntegrationsManage],
  }),

  // Onboarding OAuth Callback - accessible to all authenticated users
  [AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback]: new PermissionRequirement({
    policies: [],
  }),

  // Connect AI app - accessible to all authenticated users (each MCP tool call checks the user's own permissions)
  [AuthenticatedRoutes.ConnectAi]: new PermissionRequirement({
    policies: [],
  }),

  // Forbidden - accessible to all authenticated users (shows when they lack permission for requested page)
  [AuthenticatedRoutes.Forbidden]: new PermissionRequirement({
    policies: [],
  }),

  // BillingExpired - accessible to all authenticated users
  [AuthenticatedRoutes.BillingExpired]: new PermissionRequirement({
    policies: [],
  }),

  // ========== Data Reconciliation Routes ==========

  [DataReconciliationRoutes.Team]: new PermissionRequirement({
    policies: [OpsiumPolicy.IntegrationsManage],
  }),

  [DataReconciliationRoutes.Clients]: new PermissionRequirement({
    policies: [OpsiumPolicy.IntegrationsManage],
  }),

  [DataReconciliationRoutes.Projects]: new PermissionRequirement({
    policies: [OpsiumPolicy.IntegrationsManage],
  }),

  // ========== Settings Routes ==========

  // Personal account settings - accessible to all authenticated users
  [SettingsRoutes.ProfileInfo]: new PermissionRequirement({
    settings: [SettingType.ProfileInfo],
  }),

  [SettingsRoutes.Password]: new PermissionRequirement({
    settings: [SettingType.Password],
  }),

  [SettingsRoutes.Notifications]: new PermissionRequirement({
    settings: [SettingType.Notifications],
    featureFlags: ['showNotifications'],
  }),

  // Organization structure - requires viewing/editing org structure
  [SettingsRoutes.Departments]: new PermissionRequirement({
    settings: [SettingType.Departments],
  }),

  [SettingsRoutes.Seniorities]: new PermissionRequirement({
    settings: [SettingType.Seniorities],
  }),

  [SettingsRoutes.Roles]: new PermissionRequirement({
    settings: [SettingType.Roles],
  }),

  // Organization settings
  [SettingsRoutes.General]: new PermissionRequirement({
    settings: [SettingType.General],
  }),

  [SettingsRoutes.Finance]: new PermissionRequirement({
    settings: [SettingType.ExternalRates],
  }),

  [SettingsRoutes.TimeOff]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffTypesView],
    settings: [SettingType.TimeOff],
    featureFlags: ['showTimeOffs'],
  }),

  [SettingsRoutes.TimeOffDetails]: new PermissionRequirement({
    policies: [OpsiumPolicy.TimeOffTypesView],
    settings: [SettingType.TimeOff],
    featureFlags: ['showTimeOffs'],
  }),

  [SettingsRoutes.Integrations]: new PermissionRequirement({
    settings: [SettingType.Integrations],
  }),

  [SettingsRoutes.API]: new PermissionRequirement({
    // TODO[out-of-scope]: refine permission once the feature is implemented
    denyAlways: true,
    settings: [SettingType.API],
  }),

  [SettingsRoutes.CustomAttributes]: new PermissionRequirement({
    policies: [OpsiumPolicy.OrgSettingsManage],
    settings: [SettingType.CustomAttributes],
  }),

  [SettingsRoutes.Tags]: new PermissionRequirement({
    // TODO[out-of-scope]: refine permission once the feature is implemented
    denyAlways: true,
    settings: [SettingType.Tags],
  }),

  // Access management
  [SettingsRoutes.Members]: new PermissionRequirement({
    settings: [SettingType.MembersAccess],
  }),

  [SettingsRoutes.Permissions]: new PermissionRequirement({
    settings: [SettingType.Permissions],
  }),

  // Debug Notifications - accessible to all authenticated users (dev only, hidden in settings nav for prod)
  [SettingsRoutes.DebugNotifications]: new PermissionRequirement({
    policies: [],
  }),

  // Framer Mockups - accessible to all authenticated users (dev only)
  [SettingsRoutes.FramerMockups]: new PermissionRequirement({
    policies: [],
  }),

  // ========== Onboarding Routes ==========
  // All onboarding routes require FullAccess policy

  [OnboardingRoutes.OnboardingOrganization]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingIntegrations]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingTeam]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingClients]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingProjects]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingCompletion]: new PermissionRequirement({
    policies: [OpsiumPolicy.FullAccess],
  }),

  [OnboardingRoutes.OnboardingError]: new PermissionRequirement({
    // TODO[out-of-scope]: Create a separate generic error page that doesn't require onboarding permissions
    policies: [],
  }),

  // ========== Unauthenticated Routes ==========
  // All unauthenticated routes are publicly accessible

  [UnauthenticatedRoutes.Login]: new PermissionRequirement({
    policies: [],
  }),

  [UnauthenticatedRoutes.Register]: new PermissionRequirement({
    policies: [],
  }),

  [UnauthenticatedRoutes.ActivateAccount]: new PermissionRequirement({
    policies: [],
  }),

  [UnauthenticatedRoutes.ResetPassword]: new PermissionRequirement({
    policies: [],
  }),

  // ========== Common Routes ==========
  // Common routes are accessible to everyone

  [CommonRoutes.TermsOfService]: new PermissionRequirement({
    policies: [],
  }),

  [CommonRoutes.PrivacyPolicy]: new PermissionRequirement({
    policies: [],
  }),
};
